import React, { useState, useEffect, useCallback } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    CircularProgress,
    Select,
    FormControl,
    InputLabel,
    Chip,
    Button,
} from '@mui/material';
import { useNotify, usePermissions } from 'react-admin';
import { getHolidays, HolidayEntry } from '../dataProvider';
import { OverrideHolidayDialog } from './OverrideHolidayDialog';
import { ResetOverrideDialog } from './ResetOverrideDialog';

/** Names for the days of the week (1 = Monday … 7 = Sunday, ISO 8601 convention). */
const DAY_NAMES: Record<number, string> = {
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday',
    7: 'Sunday',
};

/**
 * Returns the range of selectable years for the year filter.
 *
 * Includes 2 years before the current year up to 2 years ahead, ensuring
 * the user can browse both historical and upcoming holidays.
 *
 * @returns Sorted array of year numbers (ascending).
 */
function buildYearOptions (): number[] {
    const current = new Date().getUTCFullYear();
    const years: number[] = [];
    for (let y = current - 2; y <= current + 2; y++) {
        years.push(y);
    }
    return years;
}

/**
 * Formats an ISO 8601 date string (YYYY-MM-DD) into a human-readable format.
 *
 * @param dateStr - ISO 8601 date string (e.g. `"2025-01-01"`).
 * @returns Formatted date string (e.g. `"January 1, 2025"`).
 */
function formatDate (dateStr: string): string {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
    });
}

/**
 * HolidayList component displaying a paginated table of public holidays
 * for a selectable year.
 *
 * Features:
 * - Year filter `<Select>` that re-fetches data when the year changes.
 * - MUI `<Table>` listing each holiday's date, name, and day of the week.
 * - Loading spinner while fetching data.
 * - Error notification via `useNotify` when the API call fails.
 */
export const HolidayList: React.FC = () => {
    const notify = useNotify();
    const { permissions } = usePermissions<string[]>();
    const canManage = Array.isArray(permissions) && permissions.includes('holidays.manage');
    const currentYear = new Date().getUTCFullYear();

    const [holidays, setHolidays] = useState<HolidayEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedYear, setSelectedYear] = useState<number>(currentYear);
    const [overrideTarget, setOverrideTarget] = useState<HolidayEntry | null>(null);
    const [resetTarget, setResetTarget] = useState<HolidayEntry | null>(null);

    const yearOptions = buildYearOptions();

    /**
     * Fetches public holidays for the currently selected year.
     *
     * Wrapped in `useCallback` to satisfy the `react-hooks/exhaustive-deps`
     * lint rule and prevent unnecessary re-renders.
     */
    const fetchHolidays = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getHolidays(selectedYear);
            setHolidays(data);
        } catch (error) {
            console.error('Error fetching holidays:', error);
            notify('Failed to load holidays', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [selectedYear, notify]);

    useEffect(() => {
        fetchHolidays();
    }, [fetchHolidays]);

    /**
     * Handles year selection changes from the native `<select>` control.
     *
     * @param event - The React change event carrying the new year value.
     */
    const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedYear(Number(event.target.value));
    };

    return (
        <Card>
            <CardContent>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                >
                    <Typography variant="h5" component="h1">
                        Public Holidays
                    </Typography>

                    <FormControl size="small" sx={{ width: 120 }}>
                        <InputLabel htmlFor="holiday-year-select">Year</InputLabel>
                        <Select
                            native
                            label="Year"
                            value={selectedYear}
                            onChange={handleYearChange as unknown as (event: { target: { name?: string; value: unknown } }) => void}
                            inputProps={{
                                id: 'holiday-year-select',
                                'data-testid': 'year-select',
                            }}
                        >
                            {yearOptions.map((year) => (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {loading ? (
                    <Box display="flex" justifyContent="center" mt={4} mb={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper}>
                        <Table aria-label={`Public holidays for ${selectedYear}`}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <strong>Date</strong>
                                    </TableCell>
                                    <TableCell>
                                        <strong>Holiday Name</strong>
                                    </TableCell>
                                    <TableCell>
                                        <strong>Day of Week</strong>
                                    </TableCell>
                                    <TableCell>
                                        <strong>Status</strong>
                                    </TableCell>
                                    <TableCell>
                                        <strong>Reason</strong>
                                    </TableCell>
                                    {canManage && (
                                        <TableCell>
                                            <strong>Action</strong>
                                        </TableCell>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {holidays.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={canManage ? 6 : 5} align="center">
                                            No public holidays found for {selectedYear}.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    holidays.map((holiday) => (
                                        <TableRow key={holiday.date}>
                                            <TableCell
                                                sx={holiday.is_overridden ? { textDecoration: 'line-through' } : undefined}
                                            >
                                                {formatDate(holiday.date)}
                                            </TableCell>
                                            <TableCell
                                                sx={holiday.is_overridden ? { textDecoration: 'line-through' } : undefined}
                                            >
                                                {holiday.holiday_name}
                                            </TableCell>
                                            <TableCell>
                                                {DAY_NAMES[holiday.day_of_week] ?? `Day ${holiday.day_of_week}`}
                                            </TableCell>
                                            <TableCell>
                                                {holiday.is_overridden ? (
                                                    <Chip label="Not a holiday" size="small" color="default" data-testid={`status-chip-${holiday.date}`} />
                                                ) : (
                                                    <Chip label="Holiday" size="small" color="primary" data-testid={`status-chip-${holiday.date}`} />
                                                )}
                                            </TableCell>
                                            <TableCell>{holiday.override_reason ?? ''}</TableCell>
                                            {canManage && (
                                                <TableCell>
                                                    {holiday.is_overridden ? (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => setResetTarget(holiday)}
                                                            data-testid={`reset-btn-${holiday.date}`}
                                                        >
                                                            Reset
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="warning"
                                                            onClick={() => setOverrideTarget(holiday)}
                                                            data-testid={`override-btn-${holiday.date}`}
                                                        >
                                                            Mark as not a holiday
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>

            <OverrideHolidayDialog
                open={overrideTarget !== null}
                date={overrideTarget?.date ?? null}
                holidayName={overrideTarget?.holiday_name ?? ''}
                onClose={() => setOverrideTarget(null)}
                onSuccess={() => {
                    setOverrideTarget(null);
                    fetchHolidays();
                }}
            />

            <ResetOverrideDialog
                open={resetTarget !== null}
                date={resetTarget?.date ?? null}
                holidayName={resetTarget?.holiday_name ?? ''}
                onClose={() => setResetTarget(null)}
                onSuccess={() => {
                    setResetTarget(null);
                    fetchHolidays();
                }}
            />
        </Card>
    );
};
